import React, { useState } from 'react';
import classnames from 'classnames';
import { useValueChange } from '@leverege/ui-hooks';
import { Pane, Label, NumericInput, IdGen, Popup, Text, Checkbox } from '@leverege/ui-elements';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import Symbolizers from "../../shared/Symbolizers.js";
import Model from "../../shared/renderers/UserPositionModel.js";
import S from './Grid.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.dataViewers.map.UserPositionEditor');
function UserPositionEditor(props) {
  const {
    value
  } = props;
  const onModelChange = useValueChange(Model, props);
  const [ids] = useState(IdGen.createIds('attr', 15));
  let cid = 0;
  const scalesWithZoom = Model.getScalesWithZoom(value);
  const objectType = 'userPosition';
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "editor",
    className: S.grid,
    children: [/*#__PURE__*/_jsx(Checkbox, {
      id: ids[cid++],
      containerClassName: S.fill,
      label: t('visible'),
      eventData: "setVisible",
      value: Model.getVisible(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Text, {
      className: S.fill,
      variant: "subtitle",
      children: t('zoom')
    }), /*#__PURE__*/_jsxs("div", {
      className: classnames(S.sub, S.grid),
      children: [/*#__PURE__*/_jsx(Label, {
        variant: "input",
        htmlFor: ids[cid],
        children: t('showZoom')
      }), /*#__PURE__*/_jsx(NumericInput, {
        id: ids[cid++],
        eventData: "setShowAtZoom",
        min: 0,
        max: Math.min(25, Model.getHideAtZoom(value)),
        allowError: true,
        value: Model.getShowAtZoom(value),
        onChange: onModelChange
      }, `haz-${Model.getHideAtZoom(value) || 'n'}`), /*#__PURE__*/_jsx(Label, {
        variant: "input",
        htmlFor: ids[cid],
        children: t('hideZoom')
      }), /*#__PURE__*/_jsx(NumericInput, {
        id: ids[cid++],
        min: Math.max(0, Model.getShowAtZoom(value)),
        max: 25,
        eventData: "setHideAtZoom",
        allowError: true,
        value: Model.getHideAtZoom(value),
        onChange: onModelChange
      }, `saz-${Model.getShowAtZoom(value) || 'n'}`), /*#__PURE__*/_jsx(Label, {
        variant: "input",
        htmlFor: ids[cid],
        children: t('fadeInOutZoom')
      }), /*#__PURE__*/_jsx(NumericInput, {
        id: ids[cid++],
        min: 0,
        max: 10,
        eventData: "setFadeZoom",
        allowError: true,
        float: true,
        value: Model.getFadeZoom(value),
        onChange: onModelChange
      })]
    }), /*#__PURE__*/_jsx(Popup, {
      title: t('help'),
      icon: tIcon('helpIcon', 'fa fa-question-circle fa-fw'),
      children: /*#__PURE__*/_jsxs("div", {
        className: S.pad,
        children: [/*#__PURE__*/_jsx(Text, {
          className: classnames(S.fill, S.hint),
          variant: "caption",
          children: t('settingZoomDescription')
        }), /*#__PURE__*/_jsx(Text, {
          className: S.fill,
          variant: "subtitle",
          children: t('zoomLevel')
        }), /*#__PURE__*/_jsxs("div", {
          className: S.grid,
          style: {
            columnGap: 'var( --size-padXLarge, 32px)'
          },
          children: [/*#__PURE__*/_jsx("span", {
            children: "0"
          }), /*#__PURE__*/_jsx("span", {
            children: t('0Zoom')
          }), /*#__PURE__*/_jsx("span", {
            children: "3"
          }), /*#__PURE__*/_jsx("span", {
            children: t('3Zoom')
          }), /*#__PURE__*/_jsx("span", {
            children: "4"
          }), /*#__PURE__*/_jsx("span", {
            children: t('4Zoom')
          }), /*#__PURE__*/_jsx("span", {
            children: "6"
          }), /*#__PURE__*/_jsx("span", {
            children: t('6Zoom')
          }), /*#__PURE__*/_jsx("span", {
            children: "10"
          }), /*#__PURE__*/_jsx("span", {
            children: t('10Zoom')
          }), /*#__PURE__*/_jsx("span", {
            children: "15"
          }), /*#__PURE__*/_jsx("span", {
            children: t('15Zoom')
          }), /*#__PURE__*/_jsx("span", {
            children: "18"
          }), /*#__PURE__*/_jsx("span", {
            children: t('18Zoom')
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(Text, {
      className: S.fill,
      variant: "subtitle",
      children: t('appearance')
    }), /*#__PURE__*/_jsxs("div", {
      className: classnames(S.grid, S.fill, S.sub),
      children: [/*#__PURE__*/_jsx(Label, {
        htmlFor: ids[cid],
        children: t('symbol')
      }), ModelTypeSelector.createSelectorAndEditor({
        editorProps: {
          id: ids[cid++],
          ...props,
          objectType
        },
        modelKey: 'symbolizer',
        values: Symbolizers.getSymbolizersFor(objectType),
        value: Model.getSymbolizer(value),
        eventData: 'setSymbolizer',
        onChange: onModelChange
      }, S.fill)]
    }), /*#__PURE__*/_jsx(Text, {
      className: S.fill,
      variant: "subtitle",
      children: t('scale')
    }), /*#__PURE__*/_jsxs("div", {
      className: classnames(S.grid, S.fill, S.sub),
      children: [/*#__PURE__*/_jsx(Label, {
        variant: "input",
        htmlFor: ids[cid],
        children: t('normal')
      }), /*#__PURE__*/_jsx(NumericInput, {
        id: ids[cid++],
        min: 0.1,
        max: 10,
        float: true,
        eventData: "setScale",
        value: Model.getScale(value),
        onChange: onModelChange
      }), /*#__PURE__*/_jsx(Checkbox, {
        id: ids[cid++],
        containerClassName: S.fill,
        label: t('scalesWithZoom'),
        eventData: "setScalesWithZoom",
        value: scalesWithZoom,
        onChange: onModelChange
      }), scalesWithZoom && /*#__PURE__*/_jsxs("div", {
        className: classnames(S.grid, S.fill, S.sub),
        children: [/*#__PURE__*/_jsxs("div", {
          className: S.grid,
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('fromZoom')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setScaleFromZoom",
            value: Model.getScaleFromZoom(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('toZoom')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0.1,
            max: 10,
            float: true,
            eventData: "setScaleToZoom",
            value: Model.getScaleToZoom(value),
            onChange: onModelChange
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: S.grid,
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('factor')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 10,
            float: true,
            eventData: "setScaleFromFactor",
            value: Model.getScaleFromFactor(value),
            onChange: onModelChange
          }), /*#__PURE__*/_jsx(Label, {
            variant: "input",
            htmlFor: ids[cid],
            children: t('factor')
          }), /*#__PURE__*/_jsx(NumericInput, {
            id: ids[cid++],
            min: 0,
            max: 10,
            float: true,
            eventData: "setScaleToFactor",
            value: Model.getScaleToFactor(value),
            onChange: onModelChange
          })]
        })]
      })]
    })]
  });
}
export default /*#__PURE__*/React.memo(UserPositionEditor);